/* ─── Shared icon helpers ──────────────────────────────────── */

function IconColorDot({ bg }) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
    >
      <circle cx="8" cy="8" r="6.5" fill={bg} />
    </svg>
  );
}

function IconRemoveHighlight() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
    >
      <circle
        cx="8"
        cy="8"
        r="6.5"
        stroke="currentColor"
        strokeWidth="1.4"
        opacity="0.55"
      />
      <line
        x1="5.5"
        y1="5.5"
        x2="10.5"
        y2="10.5"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
      <line
        x1="10.5"
        y1="5.5"
        x2="5.5"
        y2="10.5"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
    </svg>
  );
}

export function IconBookmark() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M3 2h8v10L7 9.5 3 12V2z"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function IconAskAI() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M2 2.5h10v7H8l-3 2V9.5H2z"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinejoin="round"
      />
      <path
        d="M8.2 3.8 L6.1 6.5 L7.6 6.5 L5.8 9.2"
        stroke="currentColor"
        strokeWidth="1.25"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function IconExplain() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M4.5 8.8 Q3.2 7.6 3.2 5.6 a3.8 3.8 0 0 1 7.6 0 Q10.8 7.6 9.5 8.8 L9.5 10.5 L4.5 10.5 Z"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinejoin="round"
      />
      <line
        x1="4.5"
        y1="11.6"
        x2="9.5"
        y2="11.6"
        stroke="currentColor"
        strokeWidth="1.1"
        strokeLinecap="round"
      />
      <line
        x1="5.2"
        y1="12.8"
        x2="8.8"
        y2="12.8"
        stroke="currentColor"
        strokeWidth="1.1"
        strokeLinecap="round"
      />
    </svg>
  );
}

export function IconCopy() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
      aria-hidden="true"
    >
      <rect
        x="4.5"
        y="1"
        width="8"
        height="8.5"
        rx="1.2"
        stroke="currentColor"
        strokeWidth="1.3"
      />
      <path
        d="M1.5 4.5h1.5v8h7V11"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function IconCheck() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M2.5 7.5 L5.5 10.5 L11.5 4"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/* ─── Colour palette (exported for consumers that still need it) ─ */

export const HIGHLIGHT_COLORS = [
  { id: "yellow", bg: "#fde047", label: "Yellow" },
  { id: "green", bg: "#4ade80", label: "Green" },
  { id: "blue", bg: "#60a5fa", label: "Blue" },
  { id: "pink", bg: "#fb7185", label: "Pink" },
];

/* ─── Toolbar ───────────────────────────────────────────────── */

/**
 * Floating toolbar for text-selection actions.
 *
 * `onPointerDown` must call `e.preventDefault()` on the toolbar root so the browser
 * does not collapse the text selection when the user clicks a button.
 *
 * @param {{
 *   onCopy: (e: PointerEvent) => void,
 *   copyDone: boolean,
 *   style?: React.CSSProperties,
 *   onPointerDown?: (e: PointerEvent) => void,
 *   onBookmark?: () => void,
 *   onHighlight?: (color: string) => void,
 *   onRemoveHighlight?: () => void,
 * }} props
 */
export default function SelectionToolbar({
  onCopy,
  copyDone,
  style,
  onPointerDown,
  onBookmark,
  onHighlight,
  onRemoveHighlight,
  onExplain,
  onAskAI,
}) {
  return (
    <div
      className="ct__eq-toolbar"
      style={style}
      role="toolbar"
      aria-label="Actions"
      onPointerDown={onPointerDown}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Highlight colour buttons — clicking directly applies that colour */}
      {HIGHLIGHT_COLORS.map((c) => (
        <button
          key={c.id}
          className="ct__eq-tbtn ct__eq-tbtn--dot"
          title={`Highlight ${c.label}`}
          aria-label={`Highlight ${c.label}`}
          onClick={() => onHighlight?.(c.id)}
        >
          <IconColorDot bg={c.bg} />
        </button>
      ))}

      {/* Remove highlight */}
      <button
        className="ct__eq-tbtn ct__eq-tbtn--dot"
        title="Remove highlight"
        aria-label="Remove highlight from selection"
        onClick={onRemoveHighlight}
      >
        <IconRemoveHighlight />
      </button>

      <div className="ct__eq-tbtn-sep" aria-hidden="true" />

      <button
        className="ct__eq-tbtn"
        title="Bookmark"
        aria-label="Bookmark"
        onClick={onBookmark}
      >
        <IconBookmark />
      </button>

      <div className="ct__eq-tbtn-sep" aria-hidden="true" />

      <button
        className="ct__eq-tbtn"
        title="Ask AI"
        aria-label="Ask AI"
        onClick={onAskAI}
      >
        <IconAskAI />
      </button>
      {onExplain && (
        <button
          className="ct__eq-tbtn"
          title="Explain"
          aria-label="Explain selection"
          onClick={onExplain}
        >
          <IconExplain />
        </button>
      )}

      <div className="ct__eq-tbtn-sep" aria-hidden="true" />

      <button
        className={`ct__eq-tbtn${copyDone ? " ct__eq-tbtn--done" : ""}`}
        title="Copy"
        aria-label="Copy to clipboard"
        onClick={onCopy}
      >
        {copyDone ? <IconCheck /> : <IconCopy />}
      </button>
    </div>
  );
}
